/**
 * Admin Affiliate Reports Page
 * View and export affiliate performance reports
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

export default function AdminAffiliateReports() {
  const [activeTab, setActiveTab] = useState<'top' | 'breakdown' | 'conversion' | 'summary'>('top');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Top Affiliates
  const [topAffiliates, setTopAffiliates] = useState<any[]>([]);
  const [topSortBy, setTopSortBy] = useState<'earnings' | 'referrals' | 'conversions'>('earnings');

  // Commission Breakdown
  const [breakdown, setBreakdown] = useState<any>(null);

  // Conversion Analysis
  const [conversion, setConversion] = useState<any>(null);

  // System Summary
  const [summary, setSummary] = useState<any>(null);

  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadReports();
  }, [activeTab, topSortBy, dateFrom, dateTo]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      switch (activeTab) {
        case 'top':
          params.append('sortBy', topSortBy);
          const topResponse = await apiClient.get(`/admin/reports/top-affiliates?${params}`);
          setTopAffiliates(topResponse.data.topAffiliates);
          break;

        case 'breakdown':
          const breakdownResponse = await apiClient.get(`/admin/reports/commission-breakdown?${params}`);
          setBreakdown(breakdownResponse.data.breakdown);
          break;

        case 'conversion':
          const conversionResponse = await apiClient.get(`/admin/reports/conversion-analysis?${params}`);
          setConversion(conversionResponse.data.analysis);
          break;

        case 'summary':
          const summaryResponse = await apiClient.get('/admin/reports/system-summary');
          setSummary(summaryResponse.data.summary);
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      params.append('sortBy', topSortBy);

      const response = await fetch(
        `${apiClient.defaults.baseURL}/admin/reports/export/top-affiliates?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affiliate-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Failed to export report');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Affiliate Reports</h1>
          <p className="text-slate-400 mt-1">View performance analytics and export reports</p>
        </div>
        {activeTab === 'top' && (
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Export CSV</span>
          </button>
        )}
      </div>

      {/* Date Filters */}
      {activeTab !== 'summary' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600/50 focus:outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600/50 focus:outline-none focus:border-orange-500"
              />
            </div>
            <div className="flex-1" />
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50">
        <div className="border-b border-slate-700/50">
          <nav className="flex space-x-4 px-6">
            {[
              { id: 'top', label: 'Top Affiliates' },
              { id: 'breakdown', label: 'Commission Breakdown' },
              { id: 'conversion', label: 'Conversion Analysis' },
              { id: 'summary', label: 'System Summary' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-2 border-b-2 font-medium transition ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <ErrorMessage message={error} onRetry={loadReports} />
          ) : (
            <>
              {/* Top Affiliates Tab */}
              {activeTab === 'top' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Top Performing Affiliates</h3>
                    <select
                      value={topSortBy}
                      onChange={(e) => setTopSortBy(e.target.value as any)}
                      className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600/50"
                    >
                      <option value="earnings">Sort by Earnings</option>
                      <option value="referrals">Sort by Referrals</option>
                      <option value="conversions">Sort by Conversions</option>
                    </select>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700/50">
                          <th className="text-left text-slate-400 font-medium py-3 px-4">#</th>
                          <th className="text-left text-slate-400 font-medium py-3 px-4">Affiliate</th>
                          <th className="text-left text-slate-400 font-medium py-3 px-4">Code</th>
                          <th className="text-right text-slate-400 font-medium py-3 px-4">Earnings</th>
                          <th className="text-right text-slate-400 font-medium py-3 px-4">Referrals</th>
                          <th className="text-right text-slate-400 font-medium py-3 px-4">Conversions</th>
                          <th className="text-right text-slate-400 font-medium py-3 px-4">Conversion Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topAffiliates.map((item, index) => (
                          <tr key={item.affiliate.id} className="border-b border-slate-700/50/50 hover:bg-slate-700/50/30">
                            <td className="py-3 px-4 text-slate-300">{index + 1}</td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="text-white font-medium">
                                  {item.affiliate.firstName} {item.affiliate.lastName}
                                </p>
                                <p className="text-sm text-slate-400">{item.affiliate.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-slate-300 font-mono text-sm">
                              {item.affiliate.affiliateCode}
                            </td>
                            <td className="py-3 px-4 text-right text-green-400 font-bold">
                              ${item.metrics.totalEarnings.toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right text-white">
                              {item.metrics.totalReferrals}
                            </td>
                            <td className="py-3 px-4 text-right text-white">
                              {item.metrics.totalConversions}
                            </td>
                            <td className="py-3 px-4 text-right text-white">
                              {item.metrics.conversionRate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Commission Breakdown Tab */}
              {activeTab === 'breakdown' && breakdown && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white">Commission Breakdown</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* By Type */}
                    <div>
                      <h4 className="text-md font-medium text-slate-300 mb-3">By Commission Type</h4>
                      <div className="space-y-3">
                        {Object.entries(breakdown.byType).map(([type, data]: [string, any]) => (
                          <div key={type} className="bg-slate-700/50/30 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-white capitalize">{type}</span>
                              <span className="text-green-400 font-bold">${data.total.toFixed(2)}</span>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{data.count} commissions</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* By Status */}
                    <div>
                      <h4 className="text-md font-medium text-slate-300 mb-3">By Status</h4>
                      <div className="space-y-3">
                        {Object.entries(breakdown.byStatus).map(([status, data]: [string, any]) => (
                          <div key={status} className="bg-slate-700/50/30 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-white capitalize">{status}</span>
                              <span className="text-green-400 font-bold">${data.total.toFixed(2)}</span>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{data.count} commissions</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-2">Total Commissions</h4>
                    <p className="text-3xl font-bold text-white">${breakdown.total.amount.toFixed(2)}</p>
                    <p className="text-blue-200 mt-1">{breakdown.total.count} commissions</p>
                  </div>
                </div>
              )}

              {/* Conversion Analysis Tab */}
              {activeTab === 'conversion' && conversion && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white">Conversion Rate Analysis</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6">
                      <h4 className="text-blue-200 mb-2">Total Clicks</h4>
                      <p className="text-4xl font-bold text-white">{conversion.metrics.totalClicks}</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6">
                      <h4 className="text-purple-200 mb-2">Total Referrals</h4>
                      <p className="text-4xl font-bold text-white">{conversion.metrics.totalReferrals}</p>
                      <p className="text-purple-200 text-sm mt-2">
                        {conversion.metrics.clickToReferralRate}% from clicks
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6">
                      <h4 className="text-green-200 mb-2">Total Conversions</h4>
                      <p className="text-4xl font-bold text-white">{conversion.metrics.totalConversions}</p>
                      <p className="text-green-200 text-sm mt-2">
                        {conversion.metrics.conversionRate}% conversion rate
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-700/50/30 rounded-lg p-6">
                    <h4 className="text-white font-medium mb-4">Conversion Funnel</h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="w-32 text-slate-300">Clicks</div>
                        <div className="flex-1 bg-slate-600/50 rounded-full h-8 overflow-hidden">
                          <div className="bg-orange-500 h-full" style={{ width: '100%' }}></div>
                        </div>
                        <div className="w-20 text-right text-white">{conversion.metrics.totalClicks}</div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-32 text-slate-300">Referrals</div>
                        <div className="flex-1 bg-slate-600/50 rounded-full h-8 overflow-hidden">
                          <div
                            className="bg-purple-600 h-full"
                            style={{ width: `${conversion.metrics.clickToReferralRate}%` }}
                          ></div>
                        </div>
                        <div className="w-20 text-right text-white">{conversion.metrics.totalReferrals}</div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-32 text-slate-300">Conversions</div>
                        <div className="flex-1 bg-slate-600/50 rounded-full h-8 overflow-hidden">
                          <div
                            className="bg-green-600 h-full"
                            style={{ width: `${conversion.metrics.clickToConversionRate}%` }}
                          ></div>
                        </div>
                        <div className="w-20 text-right text-white">{conversion.metrics.totalConversions}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* System Summary Tab */}
              {activeTab === 'summary' && summary && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-white">System Summary</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Affiliates */}
                    <div className="bg-slate-700/50/30 rounded-lg p-4">
                      <h4 className="text-slate-400 text-sm mb-2">Affiliates</h4>
                      <p className="text-3xl font-bold text-white mb-2">{summary.affiliates.total}</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Active:</span>
                          <span className="text-green-400">{summary.affiliates.active}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Pending:</span>
                          <span className="text-yellow-400">{summary.affiliates.pending}</span>
                        </div>
                      </div>
                    </div>

                    {/* Referrals */}
                    <div className="bg-slate-700/50/30 rounded-lg p-4">
                      <h4 className="text-slate-400 text-sm mb-2">Referrals</h4>
                      <p className="text-3xl font-bold text-white mb-2">{summary.referrals.total}</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Converted:</span>
                          <span className="text-green-400">{summary.referrals.converted}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Rate:</span>
                          <span className="text-white">{summary.referrals.conversionRate}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Commissions */}
                    <div className="bg-slate-700/50/30 rounded-lg p-4">
                      <h4 className="text-slate-400 text-sm mb-2">Commissions</h4>
                      <p className="text-3xl font-bold text-green-400 mb-2">
                        ${summary.commissions.totalAmount.toFixed(2)}
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Paid:</span>
                          <span className="text-green-400">${summary.commissions.paidAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Pending:</span>
                          <span className="text-yellow-400">${summary.commissions.pendingAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payouts */}
                    <div className="bg-slate-700/50/30 rounded-lg p-4">
                      <h4 className="text-slate-400 text-sm mb-2">Payouts</h4>
                      <p className="text-3xl font-bold text-orange-400 mb-2">
                        ${summary.payouts.totalAmount.toFixed(2)}
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Completed:</span>
                          <span className="text-green-400">{summary.payouts.completed}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Pending:</span>
                          <span className="text-yellow-400">{summary.payouts.pending}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
